import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import { Redirect } from "react-router-dom";
import { Modal } from "react-bootstrap";
import "../css/Admin_css/admin-customize.css";
import COLORS from "../resources/colors/COLORS";
import myToast from "../Utils/MyToast";
import MdlSelectSession from "./MdlSelectSession";

const MODAL_RADIUS = 18;

const MODAL_Z_INDEX_STYLES = `
  .modal.show { display: block !important; opacity: 1 !important; }
  .modal.fade.show { opacity: 1 !important; }
  .mdl-rc-dialog.modal-dialog {
    z-index: 1050 !important;
    position: relative;
    margin: 0.5rem auto 1.25rem;
  }
  .mdl-rc-dialog.modal-dialog.modal-dialog-centered {
    min-height: calc(100% - 1rem);
  }
  .modal-backdrop { z-index: 1040 !important; }
  .modal-backdrop.show { opacity: 0.5 !important; }
  .modal-backdrop.fade.show { opacity: 0.5 !important; }
  .mdl-rc-dialog .modal-content {
    z-index: 1051 !important;
    position: relative;
    border-radius: ${MODAL_RADIUS}px;
    overflow: hidden;
    border: 1px solid rgba(148, 163, 184, 0.28);
    box-shadow: 0 8px 32px rgba(15, 23, 42, 0.12);
  }
  .mdl-rc-dialog .modal-header {
    border-top-left-radius: ${MODAL_RADIUS}px;
    border-top-right-radius: ${MODAL_RADIUS}px;
  }
  .mdl-rc-dialog .modal-footer {
    border-bottom-left-radius: ${MODAL_RADIUS}px;
    border-bottom-right-radius: ${MODAL_RADIUS}px;
    padding: 8px 16px;
    min-height: 0;
  }
  .mdl-rc-dialog .close {
    opacity: 0.9;
    text-shadow: none;
  }
`;

function getConcernedExam(item) {
  const mExam = item.exams_list;
  if (!mExam || mExam.length === 0) return "";
  return mExam.map((e) => e.exam_name).filter(Boolean).join(", ");
}

function sessionStatusChipStyle(status) {
  const s = String(status || "").trim().toLowerCase();
  const isActive =
    s === "active" ||
    s === "current" ||
    s === "running" ||
    s === "ongoing" ||
    status === "1";
  if (isActive) {
    return { bg: "rgba(16, 185, 129, 0.14)", color: "#047857" };
  }
  return { bg: "rgba(148, 163, 184, 0.2)", color: "#475569" };
}

function CompactSessionSelector({
  sessionName,
  sessionStatus,
  primaryColor,
  primaryColorDark,
  onOpen,
}) {
  const chip = sessionStatusChipStyle(sessionStatus);
  const label = sessionName || "Not set";
  const statusLabel = sessionStatus || "N/A";

  return (
    <button
      type="button"
      onClick={onOpen}
      title="Change session"
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 12px",
        marginBottom: 14,
        borderRadius: 16,
        border: `1px solid ${primaryColor}33`,
        background: `linear-gradient(135deg, ${COLORS.primary_color_shade0} 0%, ${COLORS.white} 100%)`,
        cursor: "pointer",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        textAlign: "left",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = `${primaryColor}66`;
        e.currentTarget.style.boxShadow = `0 4px 12px ${primaryColor}22`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = `${primaryColor}33`;
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <span
        style={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: 12,
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColorDark} 100%)`,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: COLORS.white,
          fontSize: 14,
        }}
      >
        <i className="fa fa-calendar-alt" aria-hidden />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: "block",
            fontSize: 10,
            fontWeight: 700,
            color: "#64748b",
            textTransform: "uppercase",
            letterSpacing: 0.4,
            lineHeight: 1.2,
          }}
        >
          Session
        </span>
        <span
          style={{
            display: "block",
            fontSize: 13,
            fontWeight: 700,
            color: "#0f172a",
            lineHeight: 1.3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </span>
      </span>
      <span
        style={{
          flexShrink: 0,
          fontSize: 10,
          fontWeight: 700,
          padding: "3px 8px",
          borderRadius: 999,
          background: chip.bg,
          color: chip.color,
          textTransform: "capitalize",
          maxWidth: 88,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={statusLabel}
      >
        {statusLabel}
      </span>
      <span
        style={{
          flexShrink: 0,
          fontSize: 11,
          fontWeight: 700,
          color: primaryColor,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        Change
        <i className="fa fa-chevron-right" style={{ fontSize: 9 }} aria-hidden />
      </span>
    </button>
  );
}

const MdlSelectReportCard = ({ action_close, action_select }) => {
  const loggedIn = useMemo(
    () => localStorage.getItem("token") !== null,
    []
  );

  const primaryColor = COLORS.primary_color_shade5;
  const primaryColorDark = COLORS.primary_color_shade7;

  const [loading, setLoading] = useState(false);
  const [isOpenSessionDialog, setIsOpenSessionDialog] = useState(false);
  const [mExamReportCardList, setMExamReportCardList] = useState([]);
  const [reportCardId, setReportCardId] = useState("");
  const [reportCardName, setReportCardName] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionStatus, setSessionStatus] = useState("");
  const [selectedPosition, setSelectedPosition] = useState(0);

  const applySelectionFromList = useCallback((list, position) => {
    if (!list || list.length === 0) {
      setReportCardId("");
      setReportCardName("");
      return;
    }
    const item = list[position] ?? list[0];
    const pos = list[position] ? position : 0;
    setSelectedPosition(pos);
    setReportCardId(item.report_card_id);
    setReportCardName(item.report_card_name);
    if (item.session_id) {
      setSessionId(item.session_id);
    }
  }, []);

  const getReportCardList = useCallback(
    (activeSessionId) => {
      if (!activeSessionId) return;

      setLoading(true);
      const SCHOOL_CODE = SessionManager.getSchoolCode();
      const URL =
        process.env.REACT_APP_SERVER_NAME +
        process.env.REACT_APP_TARGET_DIR +
        "getSchoolRepordCardList.php";

      axios
        .get(URL, {
          params: {
            school_code: SCHOOL_CODE,
            session_id: activeSessionId,
          },
        })
        .then((res) => res.data)
        .then((response) => {
          setLoading(false);
          if (response.api_status === "OK") {
            const list = response.data || [];
            setMExamReportCardList(list);
            applySelectionFromList(list, 0);
          } else {
            myToast.notify_warning(
              response.api_remark || "Failed to load report cards"
            );
          }
        })
        .catch((error) => {
          setLoading(false);
          console.error("getReportCardList error:", error);
          myToast.notify_error("Failed to load report cards");
        });
    },
    [applySelectionFromList]
  );

  useEffect(() => {
    const mSession = SessionManager.getCurrentSession();
    if (mSession && mSession[0]) {
      const sid = mSession[0].session_id;
      setSessionId(sid);
      setSessionName(mSession[0].session_name);
      setSessionStatus(mSession[0].status);
      getReportCardList(sid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = useCallback(() => {
    if (action_close) action_close();
  }, [action_close]);

  const selectReportCard = useCallback(
    (id, name, position) => {
      setReportCardId(id);
      setReportCardName(name);
      setSelectedPosition(position);
    },
    []
  );

  const handleSelectedReportCard = useCallback(() => {
    if (!reportCardId) {
      myToast.notify_warning("Please select a progress report");
      return;
    }
    if (action_select) {
      action_select(reportCardId, reportCardName, sessionId);
    }
    if (action_close) action_close();
  }, [
    reportCardId,
    reportCardName,
    sessionId,
    action_select,
    action_close,
  ]);

  const handleSelectedSession = useCallback(
    (sid, sname, sstatus) => {
      setSessionId(sid);
      setSessionName(sname);
      setSessionStatus(sstatus);
      setIsOpenSessionDialog(false);
      setMExamReportCardList([]);
      setReportCardId("");
      setReportCardName("");
      setSelectedPosition(0);
      getReportCardList(sid);
    },
    [getReportCardList]
  );

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <div>
      <style>{MODAL_Z_INDEX_STYLES}</style>

      <Modal
        show={!isOpenSessionDialog}
        onHide={handleClose}
        animation={false}
        size="lg"
        dialogClassName="mdl-rc-dialog"
        aria-labelledby="mdl-select-report-card-title"
        centered
        backdrop="static"
        enforceFocus={false}
        restoreFocus={false}
      >
        <Modal.Header
          closeButton
          style={{
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColorDark} 100%)`,
            color: COLORS.white,
            borderBottom: "none",
            padding: "18px 22px",
            borderTopLeftRadius: MODAL_RADIUS,
            borderTopRightRadius: MODAL_RADIUS,
          }}
        >
          <Modal.Title
            id="mdl-select-report-card-title"
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: COLORS.white,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <i className="fa fa-file-text-o" aria-hidden />
            Select Progress Report
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: 24 }}>
          {loading ? (
            <div
              style={{
                minHeight: 200,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MyLoader />
            </div>
          ) : (
            <>
              <CompactSessionSelector
                sessionName={sessionName}
                sessionStatus={sessionStatus}
                primaryColor={primaryColor}
                primaryColorDark={primaryColorDark}
                onOpen={() => setIsOpenSessionDialog(true)}
              />

              {mExamReportCardList.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "48px 20px",
                    color: "#9ca3af",
                    borderRadius: 14,
                    background: "#f9fafb",
                    border: "1px dashed #e5e7eb",
                  }}
                >
                  <i
                    className="fa fa-inbox"
                    style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }}
                    aria-hidden
                  />
                  <div style={{ fontSize: 16, fontWeight: 600 }}>
                    No progress reports available
                  </div>
                  <div style={{ fontSize: 14, marginTop: 8 }}>
                    Try another session or check configuration
                  </div>
                </div>
              ) : (
                <div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#374151",
                      marginBottom: 16,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    <i
                      className="fa fa-list"
                      style={{ marginRight: 8, color: primaryColor }}
                      aria-hidden
                    />
                    Select a report ({mExamReportCardList.length} available)
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      maxHeight: 360,
                      overflowY: "auto",
                      padding: 4,
                      borderRadius: 14,
                    }}
                  >
                    {mExamReportCardList.map((item, index) => {
                      const isSelected = selectedPosition === index;
                      const examsLabel = getConcernedExam(item);
                      return (
                        <button
                          key={item.report_card_id || index}
                          type="button"
                          onClick={() =>
                            selectReportCard(
                              item.report_card_id,
                              item.report_card_name,
                              index
                            )
                          }
                          style={{
                            width: "100%",
                            background: isSelected
                              ? `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColorDark} 100%)`
                              : COLORS.white,
                            border: `2px solid ${isSelected ? primaryColor : "#e5e7eb"}`,
                            borderRadius: 14,
                            padding: "14px 16px",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            textAlign: "left",
                            position: "relative",
                            boxShadow: isSelected
                              ? `0 6px 16px ${primaryColor}30`
                              : "0 1px 3px rgba(0,0,0,0.06)",
                          }}
                        >
                          {isSelected && (
                            <span
                              style={{
                                position: "absolute",
                                top: 10,
                                right: 12,
                                width: 22,
                                height: 22,
                                borderRadius: "50%",
                                background: "rgba(255,255,255,0.25)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: COLORS.white,
                                fontSize: 11,
                              }}
                            >
                              <i className="fa fa-check" aria-hidden />
                            </span>
                          )}
                          <div
                            style={{
                              fontSize: 15,
                              fontWeight: 700,
                              color: isSelected ? COLORS.white : "#111827",
                              paddingRight: isSelected ? 28 : 0,
                            }}
                          >
                            {item.report_card_name}
                          </div>
                          {examsLabel ? (
                            <div
                              style={{
                                fontSize: 12,
                                marginTop: 6,
                                color: isSelected
                                  ? "rgba(255,255,255,0.9)"
                                  : "#6b7280",
                                lineHeight: 1.4,
                              }}
                            >
                              <i
                                className="fa fa-graduation-cap"
                                style={{ marginRight: 6 }}
                                aria-hidden
                              />
                              {examsLabel}
                            </div>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer
          style={{
            borderTop: "1px solid #e5e7eb",
            padding: "8px 16px",
            background: "#f9fafb",
            borderBottomLeftRadius: MODAL_RADIUS,
            borderBottomRightRadius: MODAL_RADIUS,
            minHeight: 0,
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            style={{
              background: COLORS.white,
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              padding: "6px 18px",
              fontSize: 13,
              fontWeight: 600,
              color: "#6b7280",
              cursor: "pointer",
              marginRight: 8,
              lineHeight: 1.25,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSelectedReportCard}
            disabled={!reportCardId || loading}
            style={{
              background:
                reportCardId && !loading
                  ? `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColorDark} 100%)`
                  : "#d1d5db",
              border: "none",
              borderRadius: 10,
              padding: "6px 22px",
              fontSize: 13,
              fontWeight: 600,
              color: COLORS.white,
              cursor: reportCardId && !loading ? "pointer" : "not-allowed",
              display: "flex",
              alignItems: "center",
              gap: 6,
              lineHeight: 1.25,
              boxShadow:
                reportCardId && !loading
                  ? `0 4px 12px ${primaryColor}40`
                  : "none",
            }}
          >
            <i className="fa fa-check-circle" aria-hidden />
            Done
          </button>
        </Modal.Footer>
      </Modal>

      {isOpenSessionDialog && (
        <MdlSelectSession
          stacked
          action={() => setIsOpenSessionDialog(false)}
          action2={handleSelectedSession}
        />
      )}
    </div>
  );
};

export default MdlSelectReportCard;
